import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { ComponentStore } from '@ngrx/component-store';
import { GuidedTour } from '../model/guided-tour';

export interface GuidedToursState {
  guidedTours: GuidedTourState[];
  activeSection: null | ActiveSectionState;
  endDialog: null | ActiveSectionState;
}

export type GuidedTourState = {
  id: string;
  sections: string[];
  completedSections: string[];
  remainingSections: string[];
  completed: boolean;
  widgetRemoved?: boolean;
};

export type ActiveSectionState = {
  tourId: string;
  sectionId: string;
  sectionNumber: number;
  isLast: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class GuidedTourService extends ComponentStore<GuidedToursState> {
  guidedToursState$ = this.select(state => state);
  activeSection$ = this.select(state => state.activeSection);
  guidedTour$ = (tourId: string): Observable<GuidedTourState> =>
    this.select(state => state.guidedTours.find(tour => tour.id === tourId) as GuidedTourState);
  isActiveTour$ = (tourId?: string, sectionIndex?: number): Observable<boolean> =>
    this.select(state => {
      if (!state.activeSection) {
        return false;
      }

      if (!tourId || sectionIndex === undefined) {
        return true;
      }

      const guidedTourState = state.guidedTours.find(tour => tour.id === tourId) as GuidedTourState;
      const sectionId = guidedTourState.sections[(sectionIndex ?? -1) - 1];

      return state.activeSection.tourId === tourId && state.activeSection.sectionId === sectionId;
    });
  isWidgetRemoved$ = (tourId: string): Observable<boolean> =>
    this.select(state => {
      const guidedTour = state.guidedTours.find(tour => tour.id === tourId);
      return !!guidedTour?.widgetRemoved;
    });

  private readonly userId: string;

  constructor(
    private readonly authUserService: AuthenticatedUserService,
    private readonly userService: UserService
  ) {
    super({ guidedTours: [], activeSection: null, endDialog: null });
    this.userId = this.authUserService.userId as string;
  }

  async initGuidedTour(guidedTour: GuidedTour): Promise<void> {
    const guidedTourState = await firstValueFrom(this.guidedTour$(guidedTour.id));

    if (guidedTour.id === guidedTourState?.id) {
      return;
    }

    const guidedTourProgress: number[] = await firstValueFrom(
      this.userService
        .getGuidedTourProgress(this.userId, guidedTour.id)
        .pipe(map(guidedTour => guidedTour.completedSections))
    );

    const completedSections: string[] = guidedTourProgress.map(stepIndex => guidedTour.sections[stepIndex]);

    this.setState(state => ({
      ...state,
      guidedTours: [
        ...state.guidedTours,
        {
          ...guidedTour,
          completedSections,
          remainingSections: guidedTour.sections.filter(section => !completedSections.includes(section)),
          completed: completedSections.length === guidedTour.sections.length,
        },
      ],
    }));
  }

  async startSection(tourId: string, sectionNumber: number): Promise<void> {
    const guidedTourState = await firstValueFrom(this.guidedTour$(tourId));
    this.patchState({
      activeSection: {
        tourId,
        sectionId: guidedTourState.sections[sectionNumber - 1],
        sectionNumber,
        isLast: guidedTourState.sections.length === sectionNumber,
      },
    });
  }

  cancelSection(): void {
    this.patchState({ activeSection: null });
  }

  async completeSection(tourId: string, sectionNumber: number, openEndDialog: boolean): Promise<void> {
    const guidedTour = (await firstValueFrom(this.guidedTour$(tourId))) as GuidedTourState;
    const activeSection = await firstValueFrom(this.activeSection$);
    const section = guidedTour.sections[sectionNumber - 1];

    if (guidedTour.completedSections.includes(section)) {
      this.patchState({ activeSection: null, endDialog: openEndDialog ? activeSection : null });
      return;
    }

    const updatedCompletedSections = [...guidedTour.completedSections, section];

    this.setState(state => {
      return {
        ...state,
        guidedTours: state.guidedTours.map(tourState => {
          if (tourState.id === tourId) {
            return {
              ...tourState,
              completedSections: updatedCompletedSections,
              remainingSections: guidedTour.sections.filter(section => !updatedCompletedSections.includes(section)),
            };
          }

          return tourState;
        }),
        activeSection: null,
        endDialog: openEndDialog ? activeSection : null,
      };
    });

    const sectionsToUpdate = [...guidedTour.completedSections, section].map(section =>
      guidedTour.sections.findIndex(curSection => curSection === section)
    );

    firstValueFrom(
      this.userService.setGuidedTourProgress(this.userId, tourId, { completedSections: sectionsToUpdate })
    ).then();
  }

  async startNextOrComplete(tourId: string, sectionNumber: number): Promise<boolean> {
    const state = await firstValueFrom(this.guidedTour$(tourId));
    const wouldComplete = state.remainingSections.length === 0 && !state.completed;

    if (wouldComplete) {
      this.setState(state => ({
        ...state,
        guidedTours: state.guidedTours.map(tourState =>
          tourState.id === tourId ? { ...tourState, completed: true } : tourState
        ),
      }));
      return true;
    }

    this.closeEndDialog();

    if (sectionNumber < state.sections.length) {
      await this.startSection(tourId, sectionNumber + 1);
    }

    return false;
  }

  closeEndDialog(): void {
    this.patchState({ endDialog: null });
  }

  removeWidget(tourId: string): void {
    this.setState(state => ({
      ...state,
      guidedTours: state.guidedTours.map(tourState =>
        tourState.id === tourId ? { ...tourState, widgetRemoved: true } : tourState
      ),
    }));
  }

  widgetRemoved(tourId: string): void {
    this.setState(state => ({
      ...state,
      guidedTours: state.guidedTours.map(tourState =>
        tourState.id === tourId ? { ...tourState, widgetRemoved: false } : tourState
      ),
    }));
  }
}
